import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { t } from "../../i18n";
import { backendApiCoverageStyles } from "./backend-api-coverage-styles";
import type {
  BackendApiCoverageSnapshot,
  BackendApiEndpoint,
  BackendApiTestCase,
} from "./backend-api-coverage-types";
import {
  type TestFile,
  type TestType,
  buildFiles,
  endpointMap,
  filesByTestType,
  filterFiles,
  linkedEndpoints,
  testTypes,
  typeStats,
} from "./backend-api-tests-model";
import { backendApiTestsStyles } from "./backend-api-tests-styles";

const styles = { ...backendApiCoverageStyles, ...backendApiTestsStyles };

interface BackendApiTestsBrowserProps {
  selectedFilePath?: string;
  snapshot: BackendApiCoverageSnapshot;
}

type DetailTab = "cases" | "apis";

export function BackendApiTestsBrowser({
  selectedFilePath,
  snapshot,
}: BackendApiTestsBrowserProps): React.JSX.Element {
  const files = useMemo(() => buildFiles(snapshot.tests), [snapshot.tests]);
  const selectedFromUrl = useMemo(
    () => files.find((file) => file.path === selectedFilePath) ?? null,
    [files, selectedFilePath],
  );
  const [activeType, setActiveType] = useState<TestType>(
    selectedFromUrl?.type ?? "api_unit",
  );
  const [query, setQuery] = useState("");
  const endpointsById = useMemo(
    () => endpointMap(snapshot.endpoints),
    [snapshot.endpoints],
  );
  const filesByType = useMemo(() => filesByTestType(files), [files]);
  const visibleTypes = testTypes.filter((type) => filesByType.has(type));
  const activeFiles = filesByType.get(activeType) ?? [];
  const filteredFiles = filterFiles(activeFiles, query);
  const selectedFile =
    selectedFromUrl?.type === activeType
      ? selectedFromUrl
      : (filteredFiles[0] ?? activeFiles[0] ?? null);

  useEffect(() => {
    if (selectedFromUrl) setActiveType(selectedFromUrl.type);
  }, [selectedFromUrl]);

  return (
    <section style={styles.testWorkspace}>
      <nav style={styles.testTabs} aria-label={t("api_tests.type_tabs")}>
        {visibleTypes.map((type) => {
          const stats = typeStats(filesByType.get(type) ?? []);
          return (
            <button
              key={type}
              type="button"
              onClick={() => setActiveType(type)}
              aria-pressed={type === activeType}
              style={{
                ...styles.testTab,
                ...(type === activeType ? styles.testTabActive : undefined),
              }}
            >
              <strong>{t(`api_tests.type.${type}`)}</strong>
              <span>
                {t("api_tests.tab_stats", {
                  files: stats.files,
                  tests: stats.tests,
                })}
              </span>
            </button>
          );
        })}
      </nav>

      <section style={styles.fileBrowserShell}>
        <aside style={styles.fileIndexPanel}>
          <div style={styles.apiTreeHeader}>
            <h2 style={styles.sectionTitle}>{t("api_tests.file_index")}</h2>
            <span style={styles.meta}>
              {t("api_tests.file_count", {
                files: filteredFiles.length,
                total: activeFiles.length,
              })}
            </span>
          </div>
          <p style={styles.testTypeGoal}>{t(`api_tests.goal.${activeType}`)}</p>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("api_tests.file_search_placeholder")}
            style={styles.input}
          />
          <div style={styles.fileList}>
            {filteredFiles.map((file) => (
              <FileIndexLink
                key={file.path}
                file={file}
                selected={file.path === selectedFile?.path}
              />
            ))}
          </div>
        </aside>
        <main style={styles.apiDetailPanel}>
          {selectedFile ? (
            <FileDetail
              endpointsById={endpointsById}
              file={selectedFile}
              missingSelectedPath={
                selectedFilePath ? selectedFromUrl === null : false
              }
            />
          ) : (
            <p style={styles.meta}>{t("api_tests.file_not_found")}</p>
          )}
        </main>
      </section>
    </section>
  );
}

function FileIndexLink({
  file,
  selected,
}: {
  file: TestFile;
  selected: boolean;
}): React.JSX.Element {
  return (
    <Link
      to={`/system/backend-api-tests/file/${encodeURIComponent(file.path)}`}
      style={{
        ...styles.fileIndexLink,
        ...(selected ? styles.fileIndexLinkActive : undefined),
      }}
    >
      <span style={styles.apiTreePath}>{file.path}</span>
      <span style={styles.apiTreeTitle}>
        {t("api_tests.file_tests_count", {
          tests: file.tests.length,
          apis: file.linkedEndpointIds.length,
        })}
      </span>
    </Link>
  );
}

function FileDetail({
  endpointsById,
  file,
  missingSelectedPath,
}: {
  endpointsById: Map<string, BackendApiEndpoint>;
  file: TestFile;
  missingSelectedPath: boolean;
}): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<DetailTab>("cases");
  const endpoints = linkedEndpoints(file.linkedEndpointIds, endpointsById);
  return (
    <div style={styles.detailGrid}>
      {missingSelectedPath ? (
        <p style={styles.errorNote}>{t("api_tests.file_not_found")}</p>
      ) : null}
      <section style={styles.detailSection}>
        <div style={styles.fileDetailHeader}>
          <div>
            <h2 style={styles.filePathTitle}>{file.path}</h2>
            <p style={styles.testTypeGoal}>
              {t(`api_tests.goal.${file.type}`)}
            </p>
          </div>
          <span style={styles.statusPill}>
            {t(`api_tests.type.${file.type}`)}
          </span>
        </div>
        <div style={styles.fileStats}>
          <span>
            {t("api_tests.file_total_cases", { tests: file.tests.length })}
          </span>
          <span>
            {t("api_tests.file_total_apis", { apis: endpoints.length })}
          </span>
        </div>
      </section>

      <nav style={styles.detailTabs} aria-label={t("api_tests.file_tabs")}>
        <button
          type="button"
          onClick={() => setActiveTab("cases")}
          aria-pressed={activeTab === "cases"}
          style={{
            ...styles.detailTab,
            ...(activeTab === "cases" ? styles.detailTabActive : undefined),
          }}
        >
          {t("api_tests.file_tab_cases")}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("apis")}
          aria-pressed={activeTab === "apis"}
          style={{
            ...styles.detailTab,
            ...(activeTab === "apis" ? styles.detailTabActive : undefined),
          }}
        >
          {t("api_tests.file_tab_apis")}
        </button>
      </nav>

      {activeTab === "cases" ? (
        <TestCases tests={file.tests} endpointsById={endpointsById} />
      ) : (
        <EndpointList endpoints={endpoints} />
      )}
    </div>
  );
}

function TestCases({
  endpointsById,
  tests,
}: {
  endpointsById: Map<string, BackendApiEndpoint>;
  tests: BackendApiTestCase[];
}): React.JSX.Element {
  return (
    <div style={styles.fileTestList}>
      {tests.map((testCase) => {
        const endpoints = linkedEndpoints(
          testCase.linked_endpoint_ids,
          endpointsById,
        );
        return (
          <section key={testCase.id} style={styles.testCaseCard}>
            <div style={styles.testCaseHeader}>
              <span style={styles.methodBadge}>{testCase.method}</span>
              <h3 style={styles.testCaseTitle}>{testCase.title}</h3>
            </div>
            <p style={styles.testLogic}>{testCase.logic}</p>
            {endpoints.length > 0 ? (
              <div style={styles.codeList}>
                {endpoints.map((endpoint) => (
                  <Link
                    key={endpoint.id}
                    to={`/system/backend-api-coverage/${endpoint.slug}`}
                    style={styles.pathLink}
                  >
                    {endpoint.id}
                  </Link>
                ))}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function EndpointList({
  endpoints,
}: {
  endpoints: BackendApiEndpoint[];
}): React.JSX.Element {
  if (endpoints.length === 0) {
    return <p style={styles.meta}>{t("api_tests.no_linked_apis")}</p>;
  }
  return (
    <section style={styles.detailSection}>
      <div style={styles.codeList}>
        {endpoints.map((endpoint) => (
          <Link
            key={endpoint.id}
            to={`/system/backend-api-coverage/${endpoint.slug}`}
            style={styles.pathLink}
          >
            {endpoint.id} · {endpoint.title}
          </Link>
        ))}
      </div>
    </section>
  );
}
